/**
 * React bindings of the History panel and the Versions dialog (W2-10 projects-data).
 *
 * The Versions dialog is hosted at the app level (outside the editor's DocStore provider) and the project can be
 * opened while it shows, so both read the open project's DocStore from `services.store`, following service swaps.
 */

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { DocStore } from '../../state/types';
export { useServicesStore, useSettings as useSettingsValue } from '../../app/registry/react';

export interface HistoryText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

export function useText(services: Pick<AppServices, 'i18n'>): HistoryText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(
    () => ({
      locale,
      t: (key, params) => i18n.t(key, params),
      tr: (ref) => i18n.tr(ref),
    }),
    [i18n, locale],
  );
}

/** A counter that changes whenever the store's undo history changes (entries, position, memory). */
export function useHistoryVersion(store: DocStore | null): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (store === null) return undefined;
    return store.history.subscribe(() => {
      setVersion((v) => v + 1);
    });
  }, [store]);
  return version;
}

/** Counts every committed change of the store (the current version seq moves with them). */
export function useCommitVersion(store: DocStore | null): number {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (store === null) return undefined;
    return store.subscribeCommits(() => {
      setVersion((v) => v + 1);
    });
  }, [store]);
  return version;
}

/** "Now" for relative times, refreshed every `periodMs`. */
export function useNow(periodMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(Date.now());
    }, periodMs);
    return () => {
      clearInterval(timer);
    };
  }, [periodMs]);
  return now;
}
